import React, { useState, useEffect, useRef } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import TextareaAutosize from '@mui/base/TextareaAutosize';
import AiAssistantsModal from './AiAssistantsModal';
import SaveInsightsModal from './SaveInsightsModal';
import AiSocialShareModal from './AiSocialShareModal';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ProjectsInfoModal from './ProjectsInfoModal';
import DiMarkdown from './DiMarkdown';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

function SecFilingChat({ agent, data } :any) {

  const threadsPerRow = 3;

  const [messages, setMessages] = useState([])
  const [loginRequired, setLoginRequired] = useState(false);
  const [requestTimeout, setRequestTimeout] = useState(false);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [fetchingAnswer, setFetchingAnswer] = useState(false);
  const [throttled, SetThrottled] = useState(false);
  const [sendable, setSendable] = useState(false);
  const [nextThreads, setNextThreads] = useState(threadsPerRow);
  const [serverError, setServerError] = useState(false);
  const [hoveredThreadId, setHoveredThreadId] = useState(null);
  const [threads, setThreads] = useState(data?.conversation_threads || []);
  const [deleteMessageConfirmOpen, setDeleteMessageConfirmOpen] = useState(false);
  const [deleteThreadConfirmOpen, setDeleteThreadConfirmOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [threadToDelete, setThreadToDelete] = useState(null);

  const handleMoreThreads = () => {
    setNextThreads(nextThreads + threadsPerRow);
  };

  const chatBoxRef = useRef(null);
  const newMessageRef = useRef(null);
  const questionFormRef = useRef(null);

  const previous_messages_url = "/sec_filings/" + data.accession_number + "/previous_messages?ticker=" + data.ticker;
  const question_url = "/sec_filings/" + data.accession_number + "/questions";

  function TenKInitialMessage({data} :any) {
    return (
      <div className="chat-message">
        <style>
          {`
            .chat-message .MuiCard-root:hover {
              background-color: rgba(0, 0, 0, 0.04);
              transition: background-color 0.3s ease;
            }

            .chat-message .MuiCardContent-root {
              padding: 16px;
            }

            @media (max-width: 600px) {
              .chat-message .MuiCardContent-root {
                padding: 12px;
              }
            }
          `}
        </style>

        <p>{data?.current_user ? (`Hi ${data.current_user},`) : "Hi,"}</p>
        <p>
          I am an AI Stock Market Research Analyst here to help you analyze <strong>{data.company_name}'s {data.form_type} filing</strong> from <strong>{data.last_modified_date}</strong>.
        </p>
        <p>You can ask me to answer questions or perform tasks like:</p>

        <Box sx={{
          display: 'grid',
          gap: 1.2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: 'repeat(auto-fill, minmax(400px, 1fr))'
          }
        }}>
          {[
            { prompt: `What is ${data.company_name}'s primary sources of revenue, and how have they changed over the past year?`,
              message: `What is ${data.company_name}'s primary sources of revenue, and how have they changed over the past year?` },
            { prompt: `What is the outlook for ${data.company_name} based on the management discussion in their 10-K?`,
              message: `What is the outlook for ${data.company_name} based on the management discussion in their 10-K?` },
            { prompt: `Summarize the key financial and product highlights from ${data.company_name}'s the 10-K filing?`,
              message: `Summarize the key financial and product highlights from ${data.company_name}'s the 10-K filing?` }
          ].map((item, index) => (
            <Card
              key={index}
              variant="outlined"
              sx={{
                mb: 1,
                cursor: loginRequired ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': !loginRequired ? {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                } : {}
              }}
              onClick={(e) => !loginRequired && updateSampleMessage(e, item.message)}
            >
              <CardContent>
                <Box>{item.prompt}</Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Box sx={{ mt: 3 }}>
          <p>Learn more about the <AiAssistantsModal message="AI Assistants"/> and <ProjectsInfoModal message="Projects"/>.</p>
        </Box>
      </div>
    );
  }

  function TenQInitialMessage({data} :any) {
    return (
      <div className="chat-message">
        <style>
          {`
            .chat-message .MuiCard-root:hover {
              background-color: rgba(0, 0, 0, 0.04);
              transition: background-color 0.3s ease;
            }

            .chat-message .MuiCardContent-root {
              padding: 16px;
            }

            @media (max-width: 600px) {
              .chat-message .MuiCardContent-root {
                padding: 12px;
              }
            }
          `}
        </style>

        <p>{data?.current_user ? (`Hi ${data.current_user},`) : "Hi,"}</p>
        <p>
          I am an AI Stock Market Research Analyst here to help you analyze <strong>{data.company_name}'s {data.form_type} filing</strong> from <strong>{data.last_modified_date}</strong>.
        </p>
        <p>You can ask me to answer questions or perform tasks like:</p>

        <Box sx={{
          display: 'grid',
          gap: 1.2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: 'repeat(auto-fill, minmax(400px, 1fr))'
          }
        }}>
          {[
            { prompt: `What were the main factors that contributed to ${data.company_name}'s revenue growth in the reported quarter?`,
              message: `What were the main factors that contributed to ${data.company_name}'s revenue growth in the reported quarter?` },
            { prompt: `What are the key risk factors highlighted by ${data.ticker} that could potentially impact future financial performance?`,
              message: `What are the key risk factors highlighted by ${data.ticker} that could potentially impact future financial performance?` },
            { prompt: `Summarize the key financial and product highlights from ${data.company_name}'s 10-Q filing?`,
              message: `Summarize the key financial and product highlights from ${data.company_name}'s 10-Q filing?` }
          ].map((item, index) => (
            <Card
              key={index}
              variant="outlined"
              sx={{
                mb: 1,
                cursor: loginRequired ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': !loginRequired ? {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                } : {}
              }}
              onClick={(e) => !loginRequired && updateSampleMessage(e, item.message)}
            >
              <CardContent>
                <Box>{item.prompt}</Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Box sx={{ mt: 3 }}>
          <p>Learn more about the <AiAssistantsModal message="AI Assistants"/> and <ProjectsInfoModal message="Projects"/>.</p>
        </Box>
      </div>
    );
  }

  function EightKInitialMessage({data} :any) {
    return (
      <div className="chat-message">
        <style>
          {`
            .chat-message .MuiCard-root:hover {
              background-color: rgba(0, 0, 0, 0.04);
              transition: background-color 0.3s ease;
            }

            .chat-message .MuiCardContent-root {
              padding: 16px;
            }

            @media (max-width: 600px) {
              .chat-message .MuiCardContent-root {
                padding: 12px;
              }
            }
          `}
        </style>

        <p>{data?.current_user ? (`Hi ${data.current_user},`) : "Hi,"}</p>
        <p>
          I am an AI Stock Market Research Analyst here to help you analyze <strong>{data.company_name}'s {data.form_type} filing</strong> from <strong>{data.last_modified_date}</strong>.
        </p>
        <p>You can ask me to answer questions or perform tasks like:</p>

        <Box sx={{
          display: 'grid',
          gap: 1.2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: 'repeat(auto-fill, minmax(400px, 1fr))'
          }
        }}>
          {[
            { prompt: "What triggered the filing of this 8-K? Was it a routine event or unscheduled?",
              message: "What triggered the filing of this 8-K? Was it a routine event or unscheduled?" },
            { prompt: "What are the implications of this filing to investors?",
              message: "What are the implications of this filing to investors?" },
            { prompt: "Summarize the key takeaways from the 8-K report",
              message: "Summarize the key takeaways from the 8-K report" }
          ].map((item, index) => (
            <Card
              key={index}
              variant="outlined"
              sx={{
                mb: 1,
                cursor: loginRequired ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': !loginRequired ? {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                } : {}
              }}
              onClick={(e) => !loginRequired && updateSampleMessage(e, item.message)}
            >
              <CardContent>
                <Box>{item.prompt}</Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Box sx={{ mt: 3 }}>
          <p>Learn more about the <AiAssistantsModal message="AI Assistants"/> and <ProjectsInfoModal message="Projects"/>.</p>
        </Box>
      </div>
    );
  }

  function ProxyInitialMessage({data} :any) {
    return (
      <div className="chat-message">
        <style>
          {`
            .chat-message .MuiCard-root:hover {
              background-color: rgba(0, 0, 0, 0.04);
              transition: background-color 0.3s ease;
            }

            .chat-message .MuiCardContent-root {
              padding: 16px;
            }

            @media (max-width: 600px) {
              .chat-message .MuiCardContent-root {
                padding: 12px;
              }
            }
          `}
        </style>

        <p>{data?.current_user ? (`Hi ${data.current_user},`) : "Hi,"}</p>
        <p>
          I am an AI Stock Market Research Analyst here to help you analyze <strong>{data.company_name}'s {data.form_type} filing</strong> from <strong>{data.last_modified_date}</strong>.
        </p>
        <p>You can ask me to answer questions or perform tasks like:</p>

        <Box sx={{
          display: 'grid',
          gap: 1.2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr',
            md: 'repeat(auto-fill, minmax(400px, 1fr))'
          }
        }}>
          {[
            { prompt: `What are the key proposals shareholders will vote on at ${data.company_name}'s upcoming annual meeting?`,
              message: `What are the key proposals shareholders will vote on at ${data.company_name}'s upcoming annual meeting?` },
            { prompt: `Summarize the executive compensation structure and any changes from last year for ${data.company_name}'s top executives.`,
              message: `Summarize the executive compensation structure and any changes from last year for ${data.company_name}'s top executives.` },
            { prompt: `What are the board's recommendations on the shareholder proposals, and what is their reasoning?`,
              message: `What are the board's recommendations on the shareholder proposals, and what is their reasoning?` }
          ].map((item, index) => (
            <Card
              key={index}
              variant="outlined"
              sx={{
                mb: 1,
                cursor: loginRequired ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': !loginRequired ? {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                } : {}
              }}
              onClick={(e) => !loginRequired && updateSampleMessage(e, item.message)}
            >
              <CardContent>
                <Box>{item.prompt}</Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Box sx={{ mt: 3 }}>
          <p>Learn more about the <AiAssistantsModal message="AI Assistants"/> and <ProjectsInfoModal message="Projects"/>.</p>
        </Box>
      </div>
    );
  }

  const noneStyledList={
    listStyleType:'none'
  }

  const handleDeleteThread = (e, threadId) => {
    e.stopPropagation(); // Prevent the click from triggering the openchat function
    setThreadToDelete(threadId);
    setDeleteThreadConfirmOpen(true);
  }

  const confirmDeleteThread = () => {
    fetch(`/chat_discussion_threads/${threadToDelete}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then(response => {
      if (response.ok) {
        // If the server successfully deletes the thread, update the UI
        setThreads(prevThreads => prevThreads.filter(thread => thread.id !== threadToDelete));
      } else {
        console.error('Failed to delete thread');
      }
    })
    .catch(error => {
      console.error('Error:', error);
    });
    setDeleteThreadConfirmOpen(false);
  };

  function agentInitialMessage(form_type) {
    if ((form_type == '10-K') || (form_type == '20-F') || (form_type == '40-F')) {
      return <TenKInitialMessage data={data} />
    } else if (form_type == '10-Q') {
      return <TenQInitialMessage data={data} />
    } else if (form_type == '8-K') {
      return <EightKInitialMessage data={data} />
    } else if (form_type == 'DEF 14A' || form_type == 'PRE 14A') {
      return <ProxyInitialMessage data={data} />
    } else {
      return <span> </span>
    }
  }

  function agentOnlineColor(agent_name) {
    if (agent_name == 'Lima') {
      return 'dot-indicator sm bg-success'
    } else if (agent_name == 'Saturday') {
      return 'dot-indicator sm bg-primary'
    } else if (agent_name == 'June') {
      return 'dot-indicator sm bg-info'
    } else {
      return 'dot-indicator sm bg-warning'
    }
  }

  useEffect(() => {
    fetch(previous_messages_url)
      .then(function(response) {
        if (response.status === 401) {
          setLoginRequired(true);
          return [];
        } else if (response.status === 200) {
          setLoginRequired(false);
        }
        return response.json();
      })
      .then(function(data) {
        setMessages(data);
        (data.length > 0) ? chatBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }) : null
      })
      .catch(error => {console.log(error?.message); console.log('something went wrong');})
  }, [])

  function updateMessages(e) {
    e.preventDefault();
    const message = newMessageRef.current?.value;
    if (message === '') return null;
    SetThrottled(false);
    setFetchingAnswer(true);
    setMessages([...messages, {role: "user", content: message}]);
    newMessageRef.current.value = "";
    setSendable(false);
    fetchAnswer(message);
  }

  function updateSampleMessage(e, message) {
    e.preventDefault();
    if (message === '') return null;
    setFetchingAnswer(true);
    chatBoxRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'end' });
    setMessages([...messages, {role: "user", content: message}]);
    newMessageRef.current.value = "";
    setSendable(false);
    fetchAnswer(message);
  }

  function fetchAnswer(question) {
    fetch(question_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({question: question})
    })
    .then(function(response) {
      if (response.status === 401) {
        setLoginRequired(true);
      } else if (response.status === 403) {
        setQuotaExceeded(true);
        setFetchingAnswer(false);
        return null;
      } else if (response.status === 429) {
        setFetchingAnswer(false);
        SetThrottled(true);
        return null;
      } else if (response.status === 504) {
        setFetchingAnswer(false);
        setRequestTimeout(true);
        return null;
      } else if (response.status === 500) {
        setFetchingAnswer(false);
        setServerError(true);
        return null;
      } else if (response.status === 200) {
        setLoginRequired(false);
      }
      return response.json();
    })
    .then(function(data) {
      if (data === null) return null;
      chatBoxRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'end' });
      setMessages(
        [
          ...messages,
          {role: "user", content: question, pair_id: data.pair_id},
          {role: "assistant", content: data.answer, pair_id: data.pair_id, title: data.title, public_url: data.public_url, unique_id: data.unique_id}
        ]
      );
      setFetchingAnswer(false);
    })
    .catch(error => {console.log(error?.message); console.log('something went wrong');})
  }

  function checkSendable() {
    if (newMessageRef.current?.value === '') {
      setSendable(false);
    } else {
      setSendable(true);
    }
  }

  function onEnterPress(e) {
    if(e.keyCode == 13 && e.shiftKey == false) {
      updateMessages(e);
    }
  }

  function refreshPage(e) {
    e.preventDefault();
    location.reload();
  }

  function copyMessage(e, content) {
    navigator.clipboard.writeText(content);
  }

  function deleteMessage(e, pair_id) {
    e.preventDefault();
    setMessageToDelete(pair_id);
    setDeleteMessageConfirmOpen(true);
  }

  function confirmDeleteMessage() {
    const list = messages.filter(i => i.pair_id !== messageToDelete);
    const url = "/gpt_company_messages/" + messageToDelete;
    setMessages(list);
    fetch(url, {method: 'DELETE'})
      .then(resp => resp.json)
      .then(_data => console.log('deleted'))
    setDeleteMessageConfirmOpen(false);
  }

  function downloadMessage(e, content) {
    const element = document.createElement("a");
    const file = new Blob([content], {type: 'text/plain'});
    element.href = URL.createObjectURL(file);
    element.download = `${content.slice(0, 20)}_analysis_of_${data.ticker}_generated_by_${agent}_ai_assistant.txt`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  }

  function openchat(e, url) {
    window.location.replace(url);
  }

  return (
    <div>
      <div className="page-header">
        <h3 className="page-title"> </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item"><a href="/sec_filings">SEC Filings</a></li>
            <li className="breadcrumb-item"><a href={"/ticker/" + data.ticker} >{data.ticker}</a></li>
            <li className="breadcrumb-item"><a href={"/sec_filings/" + data.ticker}>{data.ticker}'s SEC Filing</a></li>
            <li className="breadcrumb-item active" aria-current="page">{data.ticker}'s Form:{data.form_type} filed:{data.last_modified_date}</li>
          </ol>
        </nav>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div className="card chat-app-wrapper">
            <div className="row mx-0">
              <div className="col-lg-3 col-md-4 chat-list-wrapper px-0">
                <div className="sidebar-spacer">
                  <div className="px-4">
                    <div className="d-flex pt-4">
                      <div className="wrapper">
                        <h5 className="font-weight-medium mb-0 ellipsis">{data.company_name} ({data.ticker}) Form {data.form_type} Analysis</h5>
                        <p className="mb-0 text-muted ellipsis">Price: {data.price} | Change: <span style={{ color: data.change_percent_color }}>{data.change_percent}</span></p>
                        <div className="d-flex align-items-center py-1">
                          <p className="mb-0 font-weight-medium">Industry: {data.industry}</p>
                        </div>
                      </div>
                    </div>
                    <div className="pt-1">
                      <div className="d-flex align-items-center py-1">
                        <p className="mb-0 font-weight-medium"><a href={data.filing_url} target="_blank">{data.form_type} Document</a> | <a href={data.files_url} target="_blank">Files</a></p>
                      </div>
                      <div className="d-flex align-items-center py-1">
                        <p className="mb-0 font-weight-medium">Filed on: {data.last_modified_date}</p>
                      </div>
                    </div>
                  </div>
                </div>


                <div className="chat-list-item-wrapper">
                {threads.slice(0, nextThreads)?.map((thread) => (
                    <div
                      key={thread.id}
                      onClick={(e) => openchat(e, thread.url)}
                      className="list-item"
                      onMouseEnter={() => setHoveredThreadId(thread.id)}
                      onMouseLeave={() => setHoveredThreadId(null)}
                      style={{ position: 'relative' }}
                    >
                      <div className="profile-image">
                        <div className={agentOnlineColor(thread.agent)}></div>
                      </div>
                      <p className="user-name">{thread.agent}</p>
                      <p className="chat-time">{thread.date}</p>
                      <p style={{ color: '#7e7e7e' }} className="chat-text">{thread.header}</p>
                      <p>
                        {thread.message_type === 'universal_chat' && hoveredThreadId === thread.id && (
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={(e) => handleDeleteThread(e, thread.id)}
                              size="small"
                              aria-label="Delete"
                              style={{ position: 'absolute', right: '10px', top: '80%', transform: 'translateY(-50%)' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
                { nextThreads < threads.length ? (
                  <div className="sidebar-spacer">
                    <button onClick={handleMoreThreads} className="btn btn-block btn-gradient-success py-3" type="button">+ More</button>
                  </div>
                ) : null }
              </div>
              <div className="col-lg-7 col-md-8 px-0 d-flex flex-column">
                <div className="chat-container-wrapper">
                  <div className="chat-bubble incoming-chat">
                    { agentInitialMessage(data.form_type) }
                    <div className="sender-details">
                      <img className="sender-avatar img-xs rounded-circle" src="/images/june_avatar.jpg" alt="profile image" />
                      <p className="seen-text">{agent} (AI Assistant)</p>
                    </div>
                  </div>

                  {messages.map((message =>
                    <div className={(message.role == "user") ? "chat-bubble outgoing-chat" : "chat-bubble incoming-chat" }>
                      <div className="chat-message">
                        <DiMarkdown>{message.content}</DiMarkdown>
                        {(message.role == "assistant") ? (
                          <p className="font-weight-medium">
                            <Tooltip title="Delete">
                              <IconButton onClick={(e) => deleteMessage(e, message.pair_id) } size="small" aria-label="Delete">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy">
                              <IconButton onClick={(e) => copyMessage(e, message.content) } size="small" aria-label="Copy">
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton onClick={(e) => downloadMessage(e, message.content) } size="small" aria-label="Download">
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <AiSocialShareModal message={message} />
                            <SaveInsightsModal message={message} />
                          </p>
                        ) : null}
                      </div>
                      <div className="sender-details">
                        {message.role == "assistant" ? (
                          <>
                            <img className="sender-avatar img-xs rounded-circle" src="/images/june_avatar.jpg" alt="profile image" />
                            <p className="seen-text">June (AI Assistant)</p>
                          </>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  <span ref={chatBoxRef}></span>
                  {fetchingAnswer ? (
                    <div className="dot-opacity-loader">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  ) : null}
                  {requestTimeout ? (
                    <p className='card-description'>
                      <div className="alert alert-danger" role="alert">Click <a href='#' onClick={ (e) =>  refreshPage(e)  }>here</a> to see the results or refresh the page.</div>
                    </p>
                  ) : null}
                  {throttled ? (
                    <p className='card-description'>
                      <div className="alert alert-danger" role="alert">Sorry, we are experiencing high traffic; please try again in 2 minutes.</div>
                    </p>
                  ) : null}
                  {serverError ? (
                    <p className='card-description'>
                      <div className="alert alert-danger" role="alert">Sorry, something went wrong! Refresh the page or click <a href='#' onClick={ (e) =>  refreshPage(e)  }>here</a> and try again.</div>
                    </p>
                  ) : null}
                  {quotaExceeded ? (
                    <div className="quota-exceeded-prompt">
                      <style>
                        {`
                          .quota-exceeded-prompt {
                            padding: 2rem;
                            text-align: center;
                            background: linear-gradient(to right bottom, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.98));
                            border-radius: 12px;
                            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
                            margin: 1rem auto;
                            max-width: 450px;
                            border: 1px solid rgba(237, 137, 54, 0.2);
                          }

                          .quota-icon {
                            font-size: 48px;
                            color: #ed8936;
                            margin-bottom: 1rem;
                          }

                          .quota-title {
                            color: #2d3748;
                            font-size: 1.5rem;
                            font-weight: 600;
                            margin-bottom: 0.75rem;
                          }

                          .quota-message {
                            color: #718096;
                            margin-bottom: 1.5rem;
                            font-size: 1rem;
                            line-height: 1.6;
                          }

                          .quota-actions {
                            display: flex;
                            flex-direction: column;
                            gap: 0.75rem;
                            align-items: center;
                          }

                          .upgrade-button {
                            display: inline-flex;
                            align-items: center;
                            gap: 0.5rem;
                            padding: 0.75rem 1.5rem;
                            background: linear-gradient(135deg, #ed8936, #dd6b20);
                            color: white;
                            text-decoration: none;
                            border-radius: 8px;
                            font-weight: 500;
                            transition: all 0.2s ease;
                            border: none;
                            cursor: pointer;
                          }

                          .upgrade-button:hover {
                            transform: translateY(-1px);
                            box-shadow: 0 4px 12px rgba(237, 137, 54, 0.25);
                            text-decoration: none;
                            color: white;
                          }

                          .view-usage {
                            color: #718096;
                            text-decoration: none;
                            font-size: 0.9rem;
                            transition: color 0.2s ease;
                          }

                          .view-usage:hover {
                            color: #2d3748;
                            text-decoration: underline;
                          }
                        `}
                      </style>
                      <div className="quota-icon">
                        <i className="mdi mdi-alert-circle-outline"></i>
                      </div>
                      <h4 className="quota-title">Usage Limit Reached</h4>
                      <p className="quota-message">
                        You've reached your limit of questions/tasks with {agent} AI Analyst. Upgrade now to continue getting valuable market insights.
                      </p>
                      <div className="quota-actions">
                        <a href="/pricing" className="upgrade-button">
                          <i className="mdi mdi-rocket-launch-outline"></i>
                          Upgrade to Continue
                        </a>
                        <a href="/dashboard" className="view-usage">
                          View my usage details
                        </a>
                      </div>
                    </div>
                  ) : null}
                  {loginRequired ? (
                    <div className="login-prompt">
                      <style>
                        {`
                          .login-prompt {
                            padding: 1.5rem;
                            text-align: center;
                            background: rgba(255, 255, 255, 0.9);
                            border-radius: 8px;
                            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                            margin: 0.5rem auto 1rem;
                            max-width: 400px;
                            position: relative;
                            bottom: 0;
                          }

                          .login-prompt h4 {
                            color: #2c3e50;
                            margin-bottom: 0.75rem;
                            font-weight: 500;
                            font-size: 1.1rem;
                          }

                          .login-prompt p {
                            color: #7f8c8d;
                            margin-bottom: 1rem;
                            font-size: 0.9rem;
                          }

                          .login-button {
                            display: inline-block;
                            padding: 8px 20px;
                            background: #3498db;
                            color: white;
                            text-decoration: none;
                            border-radius: 6px;
                            font-weight: 500;
                            transition: all 0.2s ease;
                          }

                          .login-button:hover {
                            background: #2980b9;
                            transform: translateY(-1px);
                            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                            color: white;
                            text-decoration: none;
                          }
                        `}
                      </style>
                      <h4>Sign in Required</h4>
                      <p>Please sign in to continue using the AI assistant</p>
                      <a href="/signin" className="login-button">
                        Sign In
                      </a>
                    </div>
                  ) : (
                    <div className="mt-auto">
                      <form ref={questionFormRef} action="#" className="pt-4">
                        <div className="input-group">
                          <TextareaAutosize minRows={2} className="form-control" onKeyDown={(e) => onEnterPress(e)} onChange={ checkSendable } ref={newMessageRef} placeholder="Ask a question"/>
                          <div className="input-group-append">
                            <button type="submit"  onClick={(e) => updateMessages(e)} className="input-group-text">
                              <i className={sendable ? "mdi text-success mdi-send icon-sm" : "mdi mdi-send icon-sm" }>
                              <SendIcon fontSize='small' />
                              </i>
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add these dialogs near the end of the component */}
      <Dialog open={deleteMessageConfirmOpen} onClose={() => setDeleteMessageConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this message?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMessageConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteMessage} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteThreadConfirmOpen} onClose={() => setDeleteThreadConfirmOpen(false)}>
        <DialogTitle>Confirm Thread Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this chat thread?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteThreadConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteThread} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default SecFilingChat;
